import { CalendarDate } from '@internationalized/date';
import {
  enumLoadStatus,
  enumSalesLoadLineItemPhase,
  LoadReconciliation,
  Money,
  Weight,
  Unit,
  enumUnit,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { fileConfirmUploadAtom } from '../_graphql/mutationFileConfirmUpload';
import { loadReconciliationFileCreateAtom } from '../_graphql/mutationLoadReconciliationCreateFile';
import { loadReconciliationFileDeleteAtom } from '../_graphql/mutationLoadReconciliationFileDelete';
import { updateLoadReconciliationAtom } from '../_graphql/mutationLoadReconciliationUpdate';
import { deductionTypesQueryAtom } from '../_graphql/queryDeductionTypes';
import { freightForwardersQueryAtom } from '../_graphql/queryFreightForwarders';
import { freightLinesQueryAtom } from '../_graphql/queryFreightLines';
import { loadDetailQueryAtom } from '../_graphql/queryLoadDetail';
import { loadReconciliationQueryAtom } from '../_graphql/queryLoadReconciliation';
import { loadsListQueryAtom } from '../_graphql/queryLoadsList';
import { generateFileAccessURLMutationAtom } from '../hooks/useFileDownloadUrl';

import {
  LoadFormLineItem,
  LoadLineItemRecord as LoadLineItem,
} from '~/modules/trade/types/load';
import { LoadReconciliationRecord } from '~/modules/trade/types/load';
import { zeroMoney } from '~/modules/trade/utils/money';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

// Form types for materials reconciliation
export type ReceivedLineItemForm = {
  id: string; // Can be temp id for new items
  loadLineItemId: string;
  itemId: string | null;
  materialType: string;
  grossWeight: Weight | null;
  tareWeight: Weight | null;
  customerGrossWeight?: Weight | null;
  customerTareWeight?: Weight | null;
  unitPrice: Money | null;
  customerUnitPrice?: Money | null;
  inventoryTagId?: string;
  notes?: string;
  pricingUnit?: Unit; // Unit used for pricing calculations (separate from weight display)
};

export type ReconciliationDeductionForm = {
  id: string; // Can be temp id for new items
  receivedLineItemId?: string; // undefined for load-level deductions
  deductionTypeId: string;
  weightQuantity?: Weight | null;
  priceQuantity?: Money | null;
};

export type FreightChargeForm = {
  id: string; // Can be temp id for new items
  hauler: string;
  freightLane: string;
  deliveryNumber: string;
  accruedAmount: Money;
  chargebackAmount: Money;
  notes?: string;
};

export type MaterialsReconciliationFormState = {
  invoiceId?: string;
  // Receiving ticket fields
  ticketNumber?: string;
  loadReceivedDate?: CalendarDate;
  ticketIssueDate?: CalendarDate;
  millReceivedWeightPostDate?: CalendarDate;

  // Sales Order Data
  paymentTerm?: PaymentTerm;

  // Invoice fields
  invoiceDate?: CalendarDate;
  dueDate?: CalendarDate;
  invoiceType?: string;

  externalNotes: string;
  internalNotes: string;

  // Load Items
  loadId?: string;
  loadReconciliationId?: string;
  receivedLineItems: ReceivedLineItemForm[];
  shippedLineItems?: LoadFormLineItem[];

  // Deductions - load-level (no receivedLineItemId) and line-item-level
  deductions: ReconciliationDeductionForm[];

  // Freight charges
  freightCharges: FreightChargeForm[];

  // Receiving ticket files
  receivingTicketFiles: Array<{
    id: string;
    name: string;
    type: 'Pdf' | 'Image' | 'Text';
    url: string;
    confirmed: boolean;
    fileSize?: number;
  }>;
};

export const initialMaterialsReconciliationFormState =
  (): MaterialsReconciliationFormState => ({
    ticketNumber: undefined,
    loadReceivedDate: undefined,
    ticketIssueDate: undefined,
    millReceivedWeightPostDate: undefined,
    paymentTerm: undefined,
    invoiceDate: undefined,
    dueDate: undefined,
    invoiceType: undefined,
    externalNotes: '',
    internalNotes: '',
    receivedLineItems: [],
    deductions: [],
    freightCharges: [],
    receivingTicketFiles: [],
    shippedLineItems: [],
    loadId: '',
    loadReconciliationId: '',
  });

export const $materialsReconciliationForm =
  atom<MaterialsReconciliationFormState>(
    initialMaterialsReconciliationFormState(),
  );

// 🟣🟣🟣 PARAMS
export const $loadId = atom<string | null>(null);

export const $resetMaterialsReconciliationForm = atom(null, (get, set) => {
  set($materialsReconciliationForm, initialMaterialsReconciliationFormState());
});

export const $setInvoiceId = atom(null, (get, set, invoiceId: string) => {
  set($materialsReconciliationForm, {
    ...get($materialsReconciliationForm),
    invoiceId,
  });
});

type MapReconciliationProps = {
  formState: MaterialsReconciliationFormState;
  reconciliation?: Partial<LoadReconciliation> | LoadReconciliationRecord;
  receivedLoadLineItems?: LoadFormLineItem[];
  shippedLoadLineItems?: LoadFormLineItem[];
};
const mapReconciliationToForm = ({
  formState,
  reconciliation,
  receivedLoadLineItems,
  shippedLoadLineItems,
}: MapReconciliationProps) => {
  if (reconciliation?.receivedLineItems) {
    formState.receivedLineItems = reconciliation.receivedLineItems.map(
      (item) => ({
        ...receivedLoadLineItems?.find(
          (loadItem) => loadItem.itemId === item.loadLineItemId,
        ),
        id: item.id,
        loadLineItemId: item.loadLineItemId,
        materialType: item.materialType,
        grossWeight: item.grossWeight,
        tareWeight: item.tareWeight,
        customerGrossWeight: item.customerGrossWeight,
        customerTareWeight: item.customerTareWeight,
        unitPrice: item.unitPrice,
        customerUnitPrice: item.customerUnitPrice,
        itemId: item.itemId as string,
        notes: item.notes || undefined,
        pricingUnit: item.pricingUnit as Unit,
      }),
    );

    formState.shippedLineItems = shippedLoadLineItems;
  }

  if (reconciliation?.deductions) {
    formState.deductions = reconciliation.deductions.map((deduction) => ({
      id: deduction.id,
      receivedLineItemId: deduction.receivedLineItemId || undefined,
      deductionTypeId: deduction.deductionTypeId,
      weightQuantity: deduction.weightQuantity as Weight,
      priceQuantity: deduction.priceQuantity as Money,
    }));
  }

  if (reconciliation?.freightCharges) {
    formState.freightCharges = reconciliation.freightCharges.map((charge) => ({
      id: charge.id,
      hauler: charge.hauler,
      freightLane: charge.freightLane,
      deliveryNumber: charge.deliveryNumber,
      accruedAmount:
        typeof charge.accruedAmount === 'object' &&
        charge.accruedAmount !== null
          ? charge.accruedAmount
          : {
              ...zeroMoney(),
              amount: (charge.accruedAmount as number) || 0,
            },
      chargebackAmount:
        typeof charge.chargebackAmount === 'object' &&
        charge.chargebackAmount !== null
          ? charge.chargebackAmount
          : {
              ...zeroMoney(),
              amount: (charge.chargebackAmount as number) || 0,
            },
    }));
  }

  if (reconciliation?.files) {
    formState.receivingTicketFiles = reconciliation.files.map((file) => ({
      id: file.id,
      name: file.fileName,
      type: file.mimeType as 'Pdf' | 'Image' | 'Text',
      url: '',
      confirmed: file.status === 'Confirmed',
      fileSize: file.fileSize || undefined,
    }));
  }
};

const $loadLineItemsByPhase = (load: { lineItems: LoadFormLineItem[] }) =>
  atom(() => {
    const receivedLineItems = load.lineItems?.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Received,
    );

    const shippedLineItems = load.lineItems?.filter(
      (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
    );

    return {
      receivedLineItems,
      shippedLineItems,
    };
  });

export const $resetMaterialsReconciliationFormToLoad = atom(
  null,
  (
    get,
    set,
    load: { lineItems: LoadLineItem[] },
    reconciliation?: Pick<
      LoadReconciliation,
      'id' | 'receivedLineItems' | 'deductions' | 'freightCharges'
    >,
  ) => {
    const formState: MaterialsReconciliationFormState = {
      ...initialMaterialsReconciliationFormState(),
    };

    const lineItemsByPhase = get($loadLineItemsByPhase(load));

    mapReconciliationToForm({
      formState,
      reconciliation,
      receivedLoadLineItems: lineItemsByPhase?.receivedLineItems,
      shippedLoadLineItems: lineItemsByPhase?.shippedLineItems,
    });
    set($materialsReconciliationForm, formState);
  },
);

export const $resetMaterialsReconciliationFormToFullReconciliation = atom(
  null,
  (
    get,
    set,
    load: {
      lineItems: LoadFormLineItem[];
      salesOrder?: { paymentTerm?: PaymentTerm } | null;
    },
    reconciliation?: LoadReconciliationRecord,
  ) => {
    const loadLineItems = get($loadLineItemsByPhase(load));

    const formState: MaterialsReconciliationFormState = {
      ...initialMaterialsReconciliationFormState(),
      loadId: reconciliation?.loadId,
      loadReconciliationId: reconciliation?.id,
      ticketNumber: reconciliation?.ticketNumber || undefined,
      loadReceivedDate: maybeParseCalendarDate(
        reconciliation?.loadReceivedDate,
      ),
      ticketIssueDate: maybeParseCalendarDate(reconciliation?.ticketIssueDate),
      millReceivedWeightPostDate: maybeParseCalendarDate(
        reconciliation?.millReceivedWeightPostDate,
      ),
      paymentTerm: load.salesOrder?.paymentTerm,
      externalNotes: reconciliation?.externalNotes || '',
      invoiceType: reconciliation?.invoiceType ?? undefined,
      internalNotes: reconciliation?.internalNotes || '',
      receivingTicketFiles:
        reconciliation?.files?.map((file) => ({
          id: file.id,
          name: file.fileName,
          type: file.mimeType as 'Pdf' | 'Image' | 'Text',
          url: file.downloadUrl ?? '',
          confirmed: file.status === 'Confirmed',
          fileSize: file.fileSize || undefined,
        })) || [],
    };
    mapReconciliationToForm({
      formState,
      reconciliation,
      receivedLoadLineItems: loadLineItems?.receivedLineItems,
      shippedLoadLineItems: loadLineItems?.shippedLineItems,
    });
    set($materialsReconciliationForm, formState);
  },
);

export const $updateMaterialsReconciliationForm = atom(
  null,
  (get, set, update: Partial<MaterialsReconciliationFormState>) => {
    set($materialsReconciliationForm, {
      ...get($materialsReconciliationForm),
      ...update,
    });
  },
);

// Helper atoms for received line items
export const $addReceivedMaterialItem = atom(
  null,
  (get, set, loadLineItemId: string, load?: { lineItems: LoadLineItem[] }) => {
    const form = get($materialsReconciliationForm);

    // Find the corresponding load line item
    const loadLineItem = load?.lineItems?.find(
      (li) => li.id === loadLineItemId,
    );

    const newItem: ReceivedLineItemForm = {
      id: `temp-rli-${Date.now()}`,
      loadLineItemId,
      itemId: loadLineItem?.itemId ?? null,
      materialType: loadLineItem?.type ?? '',
      grossWeight: { __typename: 'Weight', value: 0, unit: enumUnit.Pounds },
      tareWeight: { __typename: 'Weight', value: 0, unit: enumUnit.Pounds },
      unitPrice: loadLineItem?.unitPrice ?? zeroMoney(),
      pricingUnit: (loadLineItem?.pricingUnit as Unit) ?? enumUnit.Pounds, // Use load line item's pricing unit
    };
    set($materialsReconciliationForm, {
      ...form,
      receivedLineItems: [...form.receivedLineItems, newItem],
    });
  },
);

export const $updateReceivedMaterialItem = atom(
  null,
  (
    get,
    set,
    id: string,
    update: Partial<Omit<ReceivedLineItemForm, 'id' | 'receivedLineItemId'>>,
  ) => {
    const form = get($materialsReconciliationForm);
    set($materialsReconciliationForm, {
      ...form,
      receivedLineItems: form.receivedLineItems.map((item) =>
        item.id === id ? { ...item, ...update } : item,
      ),
    });
  },
);

export const $removeReceivedMaterialItem = atom(
  null,
  (get, set, id: string) => {
    const form = get($materialsReconciliationForm);
    set($materialsReconciliationForm, {
      ...form,
      receivedLineItems: form.receivedLineItems.filter(
        (item) => item.id !== id,
      ),
    });
  },
);

// Helper atoms for deductions
export const $addDeduction = atom(
  null,
  (get, set, deduction: Omit<ReconciliationDeductionForm, 'id'>) => {
    const currentForm = get($materialsReconciliationForm);
    const newDeduction: ReconciliationDeductionForm = {
      ...deduction,
      id: `temp-ded-${Date.now()}`, // Temporary ID for new deductions
    };

    set($materialsReconciliationForm, {
      ...currentForm,
      deductions: [...currentForm.deductions, newDeduction],
    });
  },
);

export const $updateDeduction = atom(
  null,
  (
    get,
    set,
    deductionId: string,
    update: Partial<ReconciliationDeductionForm>,
  ) => {
    const currentForm = get($materialsReconciliationForm);
    const deductionIndex = currentForm.deductions.findIndex(
      (d) => d.id === deductionId,
    );

    if (deductionIndex === -1) return;

    const updatedDeductions = [...currentForm.deductions];
    updatedDeductions[deductionIndex] = {
      ...updatedDeductions[deductionIndex],
      ...update,
    };

    set($materialsReconciliationForm, {
      ...currentForm,
      deductions: updatedDeductions,
    });
  },
);

export const $removeDeduction = atom(null, (get, set, deductionId: string) => {
  const currentForm = get($materialsReconciliationForm);
  set($materialsReconciliationForm, {
    ...currentForm,
    deductions: currentForm.deductions.filter((d) => d.id !== deductionId),
  });
});

// Helper functions to get items
export const $getReceivedItemsForLoadLineItem = atom(
  (get) => (loadLineItemId: string) => {
    const form = get($materialsReconciliationForm);
    return form.receivedLineItems.filter(
      (item) => item.loadLineItemId === loadLineItemId,
    );
  },
);

export const $getLoadLevelDeductions = atom((get) => {
  const form = get($materialsReconciliationForm);
  return form.deductions.filter((d) => !d.receivedLineItemId);
});

export const $getDeductionsForReceivedLineItem = atom(
  (get) => (receivedLineItemId: string) => {
    const form = get($materialsReconciliationForm);
    return form.deductions.filter(
      (d) => d.receivedLineItemId === receivedLineItemId,
    );
  },
);

// Helper atoms for freight charges
export const $addFreightCharge = atom(
  null,
  (get, set, charge: Omit<FreightChargeForm, 'id'>) => {
    const currentForm = get($materialsReconciliationForm);
    const newCharge: FreightChargeForm = {
      ...charge,
      id: `temp-fc-${Date.now()}`, // Temporary ID for new charges
    };

    set($materialsReconciliationForm, {
      ...currentForm,
      freightCharges: [...currentForm.freightCharges, newCharge],
    });
  },
);

export const $updateFreightCharge = atom(
  null,
  (get, set, chargeId: string, update: Partial<FreightChargeForm>) => {
    const currentForm = get($materialsReconciliationForm);
    const chargeIndex = currentForm.freightCharges.findIndex(
      (c) => c.id === chargeId,
    );

    if (chargeIndex === -1) return;

    const updatedCharges = [...currentForm.freightCharges];
    updatedCharges[chargeIndex] = {
      ...updatedCharges[chargeIndex],
      ...update,
    };

    set($materialsReconciliationForm, {
      ...currentForm,
      freightCharges: updatedCharges,
    });
  },
);

export const $removeFreightCharge = atom(null, (get, set, chargeId: string) => {
  const currentForm = get($materialsReconciliationForm);
  set($materialsReconciliationForm, {
    ...currentForm,
    freightCharges: currentForm.freightCharges.filter((c) => c.id !== chargeId),
  });
});

// 🟣🟣🟣 PARAMS - Search term
export const $searchTerm = atom<string>('');

// 🟣🟣🟣 PARAMS - Date range
export const $dateRange = atom<{
  start: CalendarDate;
  end: CalendarDate;
} | null>(null);

// 🟣🟣🟣 PARAMS - Selected facility
export const $selectedFacility = atom<string | null>(null);

// 🟢🟢🟢 REMOTE DATA -> deduction types
export const $deductionTypes = deductionTypesQueryAtom();

// 🟢🟢🟢 REMOTE DATA -> freight forwarders
export const $freightForwarders = freightForwardersQueryAtom(() => ({
  variables: undefined,
}));

// 🟢🟢🟢 REMOTE DATA -> freight lines
export const $freightLines = freightLinesQueryAtom(() => ({
  variables: undefined,
}));

// 🟢🟢🟢 REMOTE DATA -> load reconciliation
export const $loadReconciliation = loadReconciliationQueryAtom((get) => ({
  variables: { loadId: get($loadId) ?? '' },
  skip: !get($loadId),
}));

// 🟢🟢🟢 REMOTE DATA -> load detail
export const $loadDetail = loadDetailQueryAtom((get) => ({
  variables: { id: get($loadId) ?? '' },
  skip: !get($loadId),
}));

// 🟢🟢🟢 REMOTE DATA -> loads pending reconciliation
export const $loadsPendingReconciliation = loadsListQueryAtom((get) => ({
  variables: {
    searchTerm: get($searchTerm),
    status: [enumLoadStatus.Received],
    fromExpectedShipDate: get($dateRange)?.start,
    toExpectedShipDate: get($dateRange)?.end,
    facilityId:
      get($selectedFacility) === 'all'
        ? undefined
        : (get($selectedFacility) ?? undefined),
    limit: 100,
  },
}));

// 🟠🟠🟠 ACTION -> UPDATE load reconciliation
export const $updateLoadReconciliation = updateLoadReconciliationAtom({
  onSuccess: (get) => {
    // Refetch reconciliation data after successful update
    get($loadReconciliation).refetch();
  },
});

// 🟠🟠🟠 ACTION -> GENERATE file access URL
export const $generateFileAccessURL = generateFileAccessURLMutationAtom();

// 🟠🟠🟠 ACTION -> CREATE load reconciliation file
export const $createLoadReconciliationFile = loadReconciliationFileCreateAtom();

// 🟠🟠🟠 ACTION -> CONFIRM load reconciliation file
export const $confirmLoadReconciliationFile = fileConfirmUploadAtom();

// 🟠🟠🟠 ACTION -> DELETE load reconciliation file
export const $deleteLoadReconciliationFile = loadReconciliationFileDeleteAtom();
